import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatDate, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Link2, CheckCircle2, XCircle, Sparkles, ArrowRight, AlertCircle, Building2 } from "lucide-react";
import { toast } from "sonner";

const scoreColor = (s) => s >= 85 ? "text-[#16A34A]" : s >= 70 ? "text-[#0020C2]" : s >= 55 ? "text-[#F59E0B]" : "text-[#DC2626]";
const scoreLabel = (s) => s >= 85 ? "Yüksek" : s >= 70 ? "İyi" : s >= 55 ? "Orta" : "Düşük";

export default function InvoiceMatchingPage() {
  const [data, setData] = useState({ pending_invoices: 0, matches: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoice-matching/suggestions");
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const confirm = async (invoiceId, txnId) => {
    setBusy(true);
    try {
      await api.post("/invoice-matching/confirm", { invoice_id: invoiceId, txn_id: txnId });
      toast.success("Fatura eşleştirildi ve ödendi olarak işaretlendi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const autoRun = async () => {
    if (!window.confirm("Yüksek skorlu (≥80 puan) ve net kazanan eşleşmeler otomatik onaylanacak. Devam?")) return;
    setAutoRunning(true);
    try {
      const { data } = await api.post("/invoice-matching/auto-run", { threshold: 80, min_gap: 15 });
      toast.success(`${data.matched} fatura otomatik eşleştirildi (${data.reviewed} önerinin arasından)`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setAutoRunning(false); }
  };

  return (
    <>
      <PageHeader
        title="Fatura ↔ Banka Eşleştirme"
        subtitle="Banka hareketlerindeki Tedarikçi Ödemelerini açık faturalarla otomatik eşleştir. Onaylanan faturalar 'ödendi' olur."
        testid="matching-header"
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="matching-refresh" onClick={load} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">Yenile</button>
            <button data-testid="matching-auto-run" onClick={autoRun} disabled={autoRunning || data.matches.length === 0} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] disabled:opacity-50">
              <Sparkles className="w-4 h-4" /> {autoRunning ? "Çalışıyor..." : "Otomatik Eşleştir"}
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Açık (Ödenmemiş) Fatura</div>
          <div className="num font-bold text-xl mt-1">{formatNumber(data.pending_invoices)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Eşleşme Önerisi</div>
          <div className="num font-bold text-xl mt-1 text-[#0020C2]">{formatNumber(data.matches.length)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Yüksek Güvenilirlik (≥85)</div>
          <div className="num font-bold text-xl mt-1 text-[#16A34A]">{formatNumber(data.matches.filter(m => (m.candidates[0]?.score || 0) >= 85).length)}</div>
        </div>
      </div>

      {loading ? (
        <div className="p-16 text-center text-sm text-neutral-500">Yükleniyor...</div>
      ) : data.matches.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] p-16 text-center text-sm text-neutral-500">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-[#16A34A]" />
          <div className="font-semibold text-neutral-700 mb-1">Eşleştirilebilecek fatura kalmadı</div>
          <div>Tüm açık faturalarınız için uygun banka hareketi bulunamadı veya hepsi zaten eşleşmiş.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {data.matches.map(m => {
            const inv = m.invoice;
            return (
              <div key={inv.id} className="bg-white border border-[#E5E5E5] rounded-md" data-testid={`match-${inv.id}`}>
                {/* Invoice header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#F5F5F5] gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-[#FEF3C7] flex items-center justify-center rounded-md shrink-0">
                      <AlertCircle className="w-5 h-5 text-[#F59E0B]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Açık Fatura</div>
                      <div className="font-heading font-bold text-base truncate">{inv.invoice_no}</div>
                      <div className="text-xs text-neutral-500 truncate">{inv.supplier_or_type} · {formatDate(inv.date)} · Vade: {formatDate(inv.due_date)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Tutar</div>
                    <div className="num font-bold text-xl">{formatTL(inv.amount)}</div>
                  </div>
                </div>

                {/* Candidates */}
                <div className="p-4 space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2 flex items-center gap-2">
                    <Link2 className="w-3.5 h-3.5" /> Aday Banka Hareketleri ({m.candidates.length})
                  </div>
                  {m.candidates.map(c => {
                    const t = c.txn;
                    return (
                      <div key={t.id} className="bg-[#FAFAFA] border border-[#F5F5F5] p-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border ${scoreColor(c.score)} border-current`}>
                              {scoreLabel(c.score)} · {c.score.toFixed(0)}
                            </span>
                            <span className="num text-xs font-semibold">{formatDate(t.date)}</span>
                            <span className="text-xs text-neutral-500">·</span>
                            <span className="text-xs text-neutral-600 truncate">{t.description?.slice(0, 100)}</span>
                          </div>
                          <div className="text-xs text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                            <Building2 className="w-3 h-3" />
                            <span className="font-semibold truncate">{t.counterparty || "-"}</span>
                            {t.category && <span className="bg-[#DBEAFE] text-[#0020C2] px-1.5 py-0.5 text-[9px] font-semibold uppercase">{t.category}</span>}
                            {t.reference && <span className="text-neutral-400">Ref: {t.reference}</span>}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="num font-bold text-base text-[#DC2626]">{formatTL(Math.abs(t.amount))}</div>
                          <button data-testid={`match-confirm-${inv.id}-${t.id}`} onClick={() => confirm(inv.id, t.id)} disabled={busy} className="mt-1 inline-flex items-center gap-1.5 bg-[#16A34A] text-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest hover:bg-[#15803D] disabled:opacity-50">
                            <CheckCircle2 className="w-3 h-3" /> Eşleştir <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
